import { InfoEmployeds } from 'components/InfoEmployeds';
import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ContentLoader from 'react-content-loader';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetOrderQuery,
  useGetResponceSelectWorkerQuery,
  useGetWorkersQuery,
} from 'redux/Api/ordersApi/ordersApi';
import { setInfoOrderDetail } from 'redux/ordersDetails/slice';
import { RootState } from 'redux/store';
import { Button } from 'ui/Button';
import { InputSearch } from 'ui/InputSearch';
import { getFormatPhone } from 'utils/getFotmatPhone';
import close from './img/iconClose.svg';
import styles from './OrderExecutor.module.scss';
import { ReactComponent as CheckIcon } from './img/check.svg';
import { ReactComponent as AlertIcon } from './img/alert.svg';
import { ReactComponent as WarningIcon } from './img/iconWarning.svg';
import { ReactComponent as IconError } from './img/iconError.svg';
import { ReactComponent as InfoIcon } from './img/iconInfo.svg';
import { ReactComponent as IconCalendar } from './img/iconCalendar.svg';
import { ReactComponent as IconWork } from './img/iconWork.svg';
import { Avatar } from 'components/Avatar';
import moment from 'moment';
import { useCanManage } from 'hooks/useCanManage';
import MinorAgeChecker from 'components/MinorAgeChecker';
import { GetWorkersArgs, ResponseGetWorkerType } from 'redux/Api/ordersApi/type';
import { EmployedsFilterParams, Filters } from 'pages/EmployedsContent/Filters/Filters';
import { useGetFiltersEmployedsQuery } from 'redux/Api/employedsApi/employedsApi';
import { useMediaQuery, useElementSize } from '@mantine/hooks';
import { MobileMenu } from 'pages/EmployedsContent/Sort/SortFilterMobile';
import { useGetProfileQuery } from 'redux/Api/profileApi/ProfileApi';
import { HandleFieldKey, HandleFieldValue } from 'components/CreateOrderForm/types';
import { Group, Loader, Switch } from '@mantine/core';
import { TabMenu } from 'components/TabMenu';
import { AppTooltip } from 'ui/AppTooltip';
import { FormatAmount } from 'ui/FormatAmount';
import { formatEndDate } from 'utils/date';
import { enumerate } from 'utils/strActions';
import useInfiniteScroll from 'react-infinite-scroll-hook';

interface OrderExecutorTypes {
  errors?: {
    employee?: string;
  };
  setContractLimit?: (contractLimit: number | null) => void;
  details?: boolean;
  clearErrorForField?: (key: string | number) => void;
}

type SearchParamsState = GetWorkersArgs | undefined;

const mobileMenuFiltersOnlyStub = () => undefined;

export const OrderExecutor: FC<OrderExecutorTypes> = ({
  errors,
  setContractLimit,
  details,
  clearErrorForField,
}) => {
  const canManage = useCanManage();
  const { infoOrderDetail } = useSelector((state: RootState) => state.ordersDetailsSlice);
  const { data: dataFilters } = useGetFiltersEmployedsQuery();
  const { data: profileData } = useGetProfileQuery();
  const [activeTab, setActiveTab] = useState<'employees' | 'responses'>('employees');
  const navigate = useNavigate();
  const { ref, width } = useElementSize();
  const isMediumWidth =
    useMediaQuery('(min-width: 1200px) and (max-width: 1406px)') ?? false;
  const isSmallWidth = useMediaQuery('(max-width: 767px)') ?? false;

  const dataFiltersMobile = useMemo(() => {
    const f = dataFilters?.filters;
    if (!f) return undefined;
    const rows = (items: { keyword: string; title: string }[]) =>
      items?.map((item, i) => ({
        id: i + 1,
        keyword: item.keyword,
        label: item.title,
      }));
    return {
      genders: rows(f.genders),
      issues: rows(f.issues),
      nationalities: rows(f.nationalities),
      contracts: rows(f.contracts),
      categories: rows(f.categories),
    };
  }, [dataFilters]);

  const dispatch = useDispatch();
  const { id } = useParams();
  const { data, isLoading } = useGetOrderQuery({ id });
  const [pageEmployees, setPageEmployees] = useState(1);
  const [pageResponses, setPageResponses] = useState(1);
  const [searchParams, setSearachParams] = useState<SearchParamsState>(undefined);
  const {
    data: workersData,
    isLoading: workersDataIsLoading,
    isFetching: workersFetching,
  } = useGetWorkersQuery({
    ...searchParams,
    page: pageEmployees,
    per_page: 20,
  });

  const needsResponseWorkers =
    Boolean(details) && data?.status?.value === 'order_published' && !data?.worker?.id;

  const {
    data: responseWorkersData,
    isLoading: isLoadingResponseWorkers,
    isFetching: isFetchingResponseWorkers,
  } = useGetResponceSelectWorkerQuery(
    { id: id!, ...searchParams, page: pageResponses, per_page: 20 },
    { skip: !id || isLoading || !needsResponseWorkers },
  );

  const [filtersParams, setFiltersParams] = useState<EmployedsFilterParams>({
    genders: [],
    issues: [],
    nationalities: [],
    categories: [],
    contracts: [],
    limit_from: undefined,
    limit_to: undefined,
    date_add: undefined,
  });

  const applySearchParams = (next: SearchParamsState) => {
    if (!setSearachParams) return;
    setPageEmployees(1);
    setPageResponses(1);
    setSearachParams(next);
  };

  const convertFiltersToApiParams = (filters: EmployedsFilterParams) => {
    return {
      genders: filters.genders,
      issues: filters.issues,
      categories: filters.categories?.map((c) => c.id),
      nationalities: filters.nationalities,
      contracts: filters.contracts,
      limit_from: filters.limit_from,
      limit_to: filters.limit_to,
      date_add: filters.date_add,
    };
  };

  const buildMergedParams = (nextSearch: string, nextFilters: EmployedsFilterParams) => {
    const hasSearch = Boolean(nextSearch.trim());
    const hasAnyFilters =
      (nextFilters.genders?.length ?? 0) > 0 ||
      (nextFilters.issues?.length ?? 0) > 0 ||
      (nextFilters.nationalities?.length ?? 0) > 0 ||
      (nextFilters.categories?.length ?? 0) > 0 ||
      (nextFilters.contracts?.length ?? 0) > 0 ||
      nextFilters.limit_from != null ||
      nextFilters.limit_to != null ||
      nextFilters.date_add?.start != null ||
      nextFilters.date_add?.end != null;

    if (!hasSearch && !hasAnyFilters) return undefined;

    const apiFilters = convertFiltersToApiParams(nextFilters);

    return {
      ...apiFilters,
      ...(hasSearch ? { search: nextSearch.trim() } : {}),
    };
  };

  const readyWorkersList = workersData?.data ?? [];
  const responseWorkersList = Array.isArray(responseWorkersData)
    ? responseWorkersData
    : responseWorkersData?.data ?? [];

  const workersList =
    details && activeTab === 'responses' ? responseWorkersList : readyWorkersList;

  const responsesCount = responseWorkersData?.meta?.assigned_count || 0;

  const orderWorker = data?.worker?.id
    ? [...readyWorkersList, ...responseWorkersList].find((el) => el.id === data.worker.id)
    : undefined;

  useEffect(() => {
    setContractLimit?.(orderWorker?.unified_contract?.limit ?? null);
  }, [orderWorker]);

  const isCanceled =
    data?.status?.value === 'order_canceled' ||
    data?.status?.value === 'order_canceled_by_platform';

  const canPickWorker = !isCanceled && (Boolean(data?.is_draft) || !data?.worker?.id);

  const selectedWorker = canPickWorker
    ? readyWorkersList.find((worker) => worker.id === infoOrderDetail.worker_id) ??
      responseWorkersList.find((worker) => worker.id === infoOrderDetail.worker_id)
    : (data?.worker as unknown as ResponseGetWorkerType | undefined);

  const [searchValue, setSearchValue] = useState('');

  const handleFieldChange = useCallback(
    (key: HandleFieldKey, value: HandleFieldValue) => {
      dispatch(setInfoOrderDetail({ key, value }));
    },
    [dispatch],
  );

  const isDraftOrCreate = !details || Boolean(data?.is_draft);

  useEffect(() => {
    if (isDraftOrCreate && infoOrderDetail.worker_id) {
      handleFieldChange('is_marketplace', false);
    }
  }, [infoOrderDetail.worker_id, handleFieldChange, isDraftOrCreate]);

  const hasNextPageEmployees = workersData?.meta
    ? workersData.meta.current_page < workersData.meta.last_page
    : false;

  const [intersectionRefEmployees, { rootRef: tableScrollRootRefEmployees }] =
    useInfiniteScroll({
      loading: Boolean(workersDataIsLoading || workersFetching),
      hasNextPage: Boolean(hasNextPageEmployees),
      onLoadMore: () => setPageEmployees((p) => p + 1),
      rootMargin: '0px 0px 160px 0px',
      disabled: activeTab !== 'employees',
    });

  const hasNextPageResponses = responseWorkersData?.meta
    ? responseWorkersData.meta.current_page < responseWorkersData.meta.last_page
    : false;

  const [intersectionRefResponses, { rootRef: tableScrollRootRefResponses }] =
    useInfiniteScroll({
      loading: Boolean(isLoadingResponseWorkers || isFetchingResponseWorkers),
      hasNextPage: Boolean(hasNextPageResponses),
      onLoadMore: () => setPageResponses((p) => p + 1),
      rootMargin: '0px 0px 160px 0px',
      disabled: activeTab !== 'responses',
    });

  const tableListRef = useRef<HTMLDivElement | null>(null);

  const setTableListRef = useCallback(
    (node: HTMLDivElement | null) => {
      tableListRef.current = node;
      if (activeTab === 'employees') {
        tableScrollRootRefEmployees(node);
        tableScrollRootRefResponses(null);
      } else {
        tableScrollRootRefResponses(node);
        tableScrollRootRefEmployees(null);
      }
    },
    [activeTab, tableScrollRootRefEmployees, tableScrollRootRefResponses],
  );

  const handleTabChange = useCallback((key: string) => {
    const nextTab = key as 'employees' | 'responses';
    setActiveTab(nextTab);
    if (tableListRef.current) {
      tableListRef.current.scrollTop = 0;
    }
  }, []);

  if (data?.status?.value === 'moderation_in_progress') return null;
  if (isLoading) return null;

  const selectWorker = (workerId: number | null) => {
    dispatch(setInfoOrderDetail({ key: 'worker_id', value: workerId }));
    if (workerId) {
      clearErrorForField?.('worker_id');
    }
  };

  const worker = selectedWorker as ResponseGetWorkerType | undefined;

  const isHasSignedContract = worker?.unified_contract?.status === 'signed_by_worker';

  const handleToggleSelling = (value?: boolean) => {
    handleFieldChange(
      'is_marketplace',
      typeof value === 'boolean' ? value : !infoOrderDetail.is_marketplace,
    );
  };

  const hideWorkersForMarketplace =
    isDraftOrCreate && Boolean(infoOrderDetail.is_marketplace);
  const showMarketplaceSwitch =
    Boolean(profileData?.features?.marketplace) &&
    isDraftOrCreate &&
    !infoOrderDetail.worker_id;

  const isListLoading =
    details && activeTab === 'responses'
      ? Boolean(isLoadingResponseWorkers)
      : workersDataIsLoading;

  const isListFetching =
    details && activeTab === 'responses'
      ? Boolean(isLoadingResponseWorkers) ||
        (isFetchingResponseWorkers && pageResponses === 1)
      : workersDataIsLoading || (workersFetching && pageEmployees === 1);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    if (!e.target.value.trim()) {
      applySearchParams(buildMergedParams('', filtersParams));
    }
  };

  const handleSearch = () => {
    applySearchParams(buildMergedParams(searchValue, filtersParams));
  };

  const displayedWorkersList = workersList;

  const handleShowLastOrder = (id: number, name: string) => {
    localStorage.setItem(
      'ordersFilters',
      JSON.stringify({
        type_ids: [],
        payment_type_ids: [],
        customer_ids: [],
        worker_ids: [{ id: id.toString(), name }],
        preset_ids: [],
        statuses: [],
        amount_from: undefined,
        amount_to: undefined,
        numbers_range: undefined,
        date_start_range: undefined,
        date_end_range: undefined,
        create_date_range: undefined,
        date_paid_range: {
          start: moment().subtract(3, 'months').format('YYYY-MM-DD'),
          end: moment().format('YYYY-MM-DD'),
        },
        act: undefined,
        receipts: undefined,
        fns_receipts_seen_status: undefined,
      }),
    );
    navigate(`/orders`);
  };

  return (
    <div ref={ref} className={styles.root}>
      <div className={styles.employees}>
        <div className={styles.employees_inner__top}>
          <p className={styles.employees_inner__title}>Исполнители</p>
          {canManage && canPickWorker && selectedWorker && (
            <Button
              theme='link'
              icon
              src={close}
              onClick={() => {
                selectWorker(null);
              }}>
              Сбросить
            </Button>
          )}
        </div>
        {showMarketplaceSwitch && (
          <Group gap='8px' h='40px'>
            <Switch
              color='#002CFB'
              styles={{
                label: {
                  fontSize: '16px',
                  lineHeight: '22px',
                  color: '#121922',
                  cursor: 'pointer',
                },
                track: {
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: infoOrderDetail.is_marketplace
                    ? '#002CFB'
                    : 'rgba(173, 191, 223, 0.40)',
                },
                thumb: {
                  border: 'none',
                },
              }}
              checked={Boolean(infoOrderDetail.is_marketplace)}
              onChange={() => {
                handleToggleSelling();
              }}
              label='Опубликовать для всех'
            />
            <AppTooltip label='Вы будете получать отклики на заказ и сможете выбрать подходящего исполнителя'>
              <InfoIcon style={{ cursor: 'pointer' }} />
            </AppTooltip>
          </Group>
        )}

        {needsResponseWorkers && !worker && (
          <TabMenu
            lists={[
              {
                idx: 1,
                key: 'employees',
                name: 'Готовы к сотрудничеству',
              },
              {
                idx: 2,
                key: 'responses',
                name: (
                  <>
                    Отклики
                    {responsesCount > 0 && (
                      <span className={styles.tabs_badge}>{responsesCount}</span>
                    )}
                  </>
                ),
              },
            ]}
            active={activeTab}
            setActive={handleTabChange}
          />
        )}

        {!hideWorkersForMarketplace ? (
          <>
            {isListLoading ? (
              <ContentLoader
                speed={2}
                height={56}
                backgroundColor={'#FFFFFF'}
                foregroundColor={'#D8E4FB8C'}>
                <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
              </ContentLoader>
            ) : worker ? (
              <div className={styles.selected}>
                <div className={styles.employee_details}>
                  <div className={styles.employee_header}>
                    <Avatar
                      name={worker?.name}
                      surname={worker?.surname}
                      avatar={worker?.worker_app?.avatar ?? worker?.avatar ?? null}
                      id={worker?.id}
                      statusActive={''}
                      height='82px'
                      width='82px'
                      isDeleted={worker?.is_deleted}
                    />
                    <div className={styles.employee_info}>
                      <div className={styles.employee_name_wrapper}>
                        <h3 className={styles.employee_name}>
                          {worker?.surname} {worker?.name} {worker?.patronymic}
                        </h3>
                        <p className={styles.employee_phone}>
                          {getFormatPhone(worker?.phone)}
                        </p>
                      </div>
                      <p className={styles.employee_inn}>ИНН {worker?.inn}</p>
                      <p className={styles.risk_level}>Риск минимальный</p>
                    </div>
                  </div>

                  <div className={styles.status_indicators}>
                    {isHasSignedContract && (
                      <div className={styles.status_item}>
                        <div className={styles.status_icon}>
                          <CheckIcon />
                        </div>
                        <p>Договор № УД-{selectedWorker?.unified_contract?.number}</p>
                      </div>
                    )}
                    {isHasSignedContract && (
                      <div className={styles.status_item}>
                        <div className={styles.status_icon}>
                          <CheckIcon />
                        </div>
                        <p>
                          Лимит по договору:{' '}
                          {selectedWorker?.unified_contract?.limit
                            ? `${
                                selectedWorker?.unified_contract?.total_orders_amount?.toLocaleString(
                                  'ru-RU',
                                ) || 0
                              } ₽ из ${selectedWorker?.unified_contract?.limit?.toLocaleString(
                                'ru-RU',
                              )} ₽`
                            : 'Не указан'}
                        </p>
                      </div>
                    )}
                    {worker?.limit && !isCanceled && (
                      <div className={styles.status_item}>
                        <div className={styles.status_icon}>
                          <CheckIcon />
                        </div>
                        <p>
                          Лимит на месяц:{' '}
                          {worker?.monthly_payment?.toLocaleString('ru-RU') || '0'} ₽ из{' '}
                          {worker?.limit?.toLocaleString('ru-RU')} ₽
                        </p>
                      </div>
                    )}
                    {!worker?.worker_app?.nationality ? (
                      <div className={styles.status_item}>
                        <div className={styles.status_icon}>
                          <AlertIcon />
                        </div>
                        <p>Гражданство неизвестно. Обратитесь в поддержку</p>
                      </div>
                    ) : worker?.worker_app?.nationality !== 'Россия' ? (
                      <div className={styles.status_item}>
                        <div className={styles.status_icon}>
                          <AlertIcon />
                        </div>
                        <p>
                          Не является гражданином России. Не забудь отправить уведомление
                          в МВД
                        </p>
                      </div>
                    ) : null}

                    <div className={styles.status_item}>
                      <div className={styles.status_icon}>
                        <CheckIcon />
                      </div>
                      <p>Является самозанятым</p>
                    </div>
                    {Boolean(worker?.has_ip) && (
                      <div className={styles.status_item}>
                        <div className={styles.status_icon}>
                          <WarningIcon />
                        </div>
                        <p>Есть открытое ИП</p>
                      </div>
                    )}
                    <div className={styles.status_item}>
                      <div className={styles.status_icon}>
                        <CheckIcon />
                      </div>
                      <p>Не состоит в списке экстремистов и террористов</p>
                    </div>

                    <div className={styles.status_item}>
                      <div className={styles.status_icon}>
                        <CheckIcon />
                      </div>
                      <p>Лимит дохода НПД 2,4 млн. не израсходован</p>
                    </div>
                    {data?.worker?.is_deleted && (
                      <div className={styles.status_item}>
                        <div className={styles.status_icon}>
                          <IconError style={{ width: '24px', height: '24px' }} />
                        </div>
                        <p style={{ color: '#e10d0d' }}>Профиль удален</p>
                      </div>
                    )}
                    <div>
                      <MinorAgeChecker data={orderWorker} showLabel={false} />
                    </div>
                  </div>

                  <p className={styles.report_date}>
                    Отчет на {moment(data?.status?.created_at).format('D MMMM YYYY')}
                  </p>
                </div>
              </div>
            ) : canPickWorker ? (
              <div>
                <div className={errors?.employee ? styles.border_error : undefined}>
                  <div className={styles.searchLine}>
                    <div style={{ width: '100%', minWidth: 0 }}>
                      <InputSearch
                        notFocus
                        value={searchValue}
                        placeholder='Искать по ИНН, ФИО или номеру телефона'
                        onChange={handleSearchChange}
                        onSearch={handleSearch}
                      />
                    </div>
                    <div className={styles.onlyMobile}>
                      <MobileMenu
                        filtersOnly
                        filtersOnlyIconOnly
                        mobileTabsSort={[]}
                        handleTabClickSort={mobileMenuFiltersOnlyStub}
                        activeSortName='default'
                        setActiveSort={mobileMenuFiltersOnlyStub}
                        dataFiltersMobile={dataFiltersMobile}
                        initialFilters={{
                          genders: filtersParams.genders ?? [],
                          issues: filtersParams.issues ?? [],
                          nationalities: filtersParams.nationalities ?? [],
                          categories: filtersParams.categories ?? [],
                          contracts: filtersParams.contracts ?? [],
                          limit_from: filtersParams.limit_from,
                          limit_to: filtersParams.limit_to,
                          date_add: filtersParams.date_add,
                        }}
                        showFilters={true}
                        onApplyFilters={(nextFilters) => {
                          setFiltersParams({
                            genders: nextFilters.genders ?? [],
                            issues: nextFilters.issues ?? [],
                            nationalities: nextFilters.nationalities ?? [],
                            categories: nextFilters.categories ?? [],
                            contracts: nextFilters.contracts ?? [],
                            limit_from: nextFilters.limit_from,
                            limit_to: nextFilters.limit_to,
                            date_add: nextFilters.date_add,
                          });
                          applySearchParams(buildMergedParams(searchValue, nextFilters));
                        }}
                        onResetFilters={() => {
                          const empty: EmployedsFilterParams = {
                            genders: [],
                            issues: [],
                            nationalities: [],
                            categories: [],
                            contracts: [],
                            limit_from: undefined,
                            limit_to: undefined,
                            date_add: undefined,
                          };
                          setFiltersParams(empty);
                          applySearchParams(buildMergedParams(searchValue, empty));
                        }}
                        mobileTabs={[]}
                        handleTabClick={mobileMenuFiltersOnlyStub}
                        activeTabName=''
                        toggleSearchOpen={mobileMenuFiltersOnlyStub}
                      />
                    </div>
                    <div className={styles.onlyDesktop}>
                      <Filters
                        dataFilters={
                          dataFilters?.filters ?? {
                            genders: [],
                            issues: [],
                            categories: [],
                            contracts: [],
                            nationalities: [],
                          }
                        }
                        notShowText={isMediumWidth || isSmallWidth}
                        showFilters={true}
                        initialFilters={{
                          genders: filtersParams.genders ?? [],
                          issues: filtersParams.issues ?? [],
                          nationalities: filtersParams.nationalities ?? [],
                          categories: filtersParams.categories ?? [],
                          contracts: filtersParams.contracts ?? [],
                          limit_from: filtersParams.limit_from,
                          limit_to: filtersParams.limit_to,
                          date_add: filtersParams.date_add,
                        }}
                        onApplyFilters={(nextFilters) => {
                          setFiltersParams({
                            genders: nextFilters.genders ?? [],
                            issues: nextFilters.issues ?? [],
                            nationalities: nextFilters.nationalities ?? [],
                            categories: nextFilters.categories ?? [],
                            contracts: nextFilters.contracts ?? [],
                            limit_from: nextFilters.limit_from,
                            limit_to: nextFilters.limit_to,
                            date_add: nextFilters.date_add,
                          });
                          applySearchParams(buildMergedParams(searchValue, nextFilters));
                        }}
                        onResetFilters={() => {
                          const empty: EmployedsFilterParams = {
                            genders: [],
                            issues: [],
                            nationalities: [],
                            categories: [],
                            contracts: [],
                            limit_from: undefined,
                            limit_to: undefined,
                            date_add: undefined,
                          };
                          setFiltersParams(empty);
                          applySearchParams(buildMergedParams(searchValue, empty));
                        }}
                      />
                    </div>
                  </div>

                  {width >= 700 &&
                    (activeTab === 'responses' && responsesCount <= 0 ? null : (
                      <div
                        className={
                          activeTab === 'employees'
                            ? styles.tableWrapperEmployees
                            : styles.tableWrapperResponses
                        }>
                        <p style={{ textAlign: 'left' }}>Исполнители</p>
                        {activeTab === 'employees' && (
                          <p style={{ textAlign: 'center' }}>Последний заказ, ₽</p>
                        )}
                        <p style={{ textAlign: 'right' }}>
                          {activeTab === 'employees'
                            ? 'Последние 3 мес, ₽'
                            : 'Выполнено / Дата регистрации'}
                        </p>
                      </div>
                    ))}

                  <div className={styles.employees_inner__table} ref={setTableListRef}>
                    {isListFetching ? (
                      <ContentLoader
                        speed={2}
                        height={'270px'}
                        width='100%'
                        className={styles.tab_loader}
                        backgroundColor={'#FFFFFF'}
                        foregroundColor={'#D8E4FB8C'}>
                        <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
                      </ContentLoader>
                    ) : (
                      <>
                        {displayedWorkersList?.length ? (
                          displayedWorkersList.map((worker) => (
                            <div
                              key={worker.id}
                              className={styles.employees_inner__table__item}
                              onClick={() => {
                                selectWorker(worker.id);
                                handleToggleSelling(false);
                              }}>
                              {width >= 700 && (
                                <div className={styles.employees_inner__table__item_user}>
                                  <div
                                    className={
                                      activeTab === 'employees'
                                        ? styles.userInfoEmployees
                                        : styles.userInfoResponses
                                    }>
                                    <InfoEmployeds
                                      status={worker.worker_app?.app_status}
                                      avatar={worker.worker_app?.avatar ?? null}
                                      id={worker.id}
                                      name={worker.name}
                                      surname={worker.surname}
                                      patronymic={worker.patronymic}
                                      phone={worker.phone ?? undefined}
                                      withPhoneIcon={false}
                                      birthdate={worker.worker_app.birthdate}
                                      nationality={worker.worker_app.nationality}
                                      showContractBanner={
                                        worker.worker_app.nationality !== 'Россия' &&
                                        worker.unified_contract?.status !==
                                          'signed_by_worker' &&
                                        activeTab === 'responses'
                                      }
                                    />
                                    {activeTab === 'employees' && (
                                      <div className={styles.lastOrder}>
                                        {worker.last_order_amount ? (
                                          <div>
                                            {FormatAmount({
                                              amount: worker.last_order_amount.toString(),
                                              compact: true,
                                            })}
                                          </div>
                                        ) : (
                                          ''
                                        )}

                                        {worker.last_order_date && (
                                          <p style={{ paddingRight: '8px' }}>
                                            {formatEndDate(worker.last_order_date)}
                                          </p>
                                        )}
                                      </div>
                                    )}

                                    <div className={styles.last3Months}>
                                      {activeTab === 'employees' ? (
                                        worker.last_3_months_orders_count ? (
                                          <div
                                            className={styles.last3MonthsCount}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleShowLastOrder(
                                                worker.id,
                                                `${worker.surname} ${worker.name} ${worker.patronymic}`,
                                              );
                                            }}>
                                            {worker.last_3_months_orders_count}{' '}
                                            {enumerate(
                                              worker.last_3_months_orders_count,
                                              ['заказ', 'заказа', 'заказов'],
                                            )}
                                          </div>
                                        ) : (
                                          ''
                                        )
                                      ) : worker.orders_count ? (
                                        <div>
                                          {worker.orders_count}{' '}
                                          {enumerate(worker.orders_count, [
                                            'заказ',
                                            'заказа',
                                            'заказов',
                                          ])}
                                        </div>
                                      ) : (
                                        <div style={{ height: '18px' }}></div>
                                      )}

                                      {activeTab === 'responses' && (
                                        <p>
                                          {formatEndDate(worker.worker_app.date_create)}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {width < 700 && (
                                <div
                                  className={
                                    styles.employees_inner__table__item_user_mobile
                                  }>
                                  <InfoEmployeds
                                    status={worker.worker_app?.app_status}
                                    avatar={worker.worker_app?.avatar ?? null}
                                    id={worker.id}
                                    name={worker.name}
                                    surname={worker.surname}
                                    patronymic={worker.patronymic}
                                    phone={worker.phone ?? undefined}
                                    withPhoneIcon={false}
                                    birthdate={worker.worker_app.birthdate}
                                    nationality={worker.worker_app.nationality}
                                  />
                                  {activeTab === 'employees' ? (
                                    <>
                                      <div className={styles.lastOrderMobile}>
                                        <IconWork />
                                        <div className={styles.lastOrderMobileInfo}>
                                          Последний заказ, ₽
                                          {worker.last_order_date && (
                                            <p>{formatEndDate(worker.last_order_date)}</p>
                                          )}
                                        </div>
                                        {worker.last_order_amount ? (
                                          <div className={styles.lastOrderMobileValue}>
                                            {FormatAmount({
                                              amount: worker.last_order_amount.toString(),
                                              compact: true,
                                            })}
                                          </div>
                                        ) : null}
                                      </div>
                                      <div className={styles.lastOrderMobile}>
                                        <IconCalendar />
                                        <div className={styles.lastOrderMobileInfo}>
                                          Последние 3 мес
                                        </div>
                                        {worker.last_3_months_orders_count ? (
                                          <div
                                            className={styles.last3MonthsCount}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleShowLastOrder(
                                                worker.id,
                                                `${worker.surname} ${worker.name} ${worker.patronymic}`,
                                              );
                                            }}>
                                            {worker.last_3_months_orders_count}{' '}
                                            {enumerate(
                                              worker.last_3_months_orders_count,
                                              ['заказ', 'заказа', 'заказов'],
                                            )}
                                          </div>
                                        ) : null}
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      {worker.worker_app.nationality !== 'Россия' &&
                                        worker.unified_contract?.status !==
                                          'signed_by_worker' && (
                                          <div className={styles.contractBannerMobile}>
                                            Требуется договор
                                          </div>
                                        )}
                                      <div className={styles.lastOrderMobile}>
                                        <IconWork />
                                        <div className={styles.lastOrderMobileInfo}>
                                          Выполнено
                                          <p>Дата регистрации</p>
                                        </div>
                                        <div className={styles.lastOrderMobileValue}>
                                          {worker.orders_count ? (
                                            <>
                                              {worker.orders_count}{' '}
                                              {enumerate(worker.orders_count, [
                                                'заказ',
                                                'заказа',
                                                'заказов',
                                              ])}
                                            </>
                                          ) : (
                                            <div style={{ height: '18px' }}></div>
                                          )}
                                          <p>
                                            {formatEndDate(worker.worker_app.date_create)}
                                          </p>
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className={styles.employees_inner__table__empty}>
                            {activeTab === 'employees'
                              ? 'Нет исполнителей'
                              : 'Пока никто не откликнулся на заказ'}
                          </div>
                        )}
                        {activeTab === 'employees' && workersFetching && (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              padding: '10px 0',
                            }}>
                            <Loader size='sm' color='#002cfb' />
                          </div>
                        )}
                        {activeTab === 'responses' && isFetchingResponseWorkers && (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              padding: '10px 0',
                            }}>
                            <Loader size='sm' color='#002cfb' />
                          </div>
                        )}
                        {activeTab === 'employees' && hasNextPageEmployees ? (
                          <div ref={intersectionRefEmployees} style={{ height: 10 }} />
                        ) : null}
                        {activeTab === 'responses' && hasNextPageResponses ? (
                          <div ref={intersectionRefResponses} style={{ height: 10 }} />
                        ) : null}
                      </>
                    )}
                  </div>
                </div>
                <p className={styles.employees_inner__infoError}>{errors?.employee}</p>
              </div>
            ) : (
              <div className={styles.employees_inner__table__empty}>
                {activeTab === 'employees'
                  ? 'Нет исполнителей'
                  : 'Пока никто не откликнулся на заказ'}
              </div>
            )}
          </>
        ) : null}
      </div>
      {!selectedWorker && (
        <div>
          {activeTab === 'employees' ? (
            <p className={styles.employees_inner__info}>
              Предложить заказ можно только готовым к сотрудничеству исполнителям
            </p>
          ) : (
            <p className={styles.employees_inner__info}>
              Выбери одного исполнителя. Остальные получат отказ автоматически
            </p>
          )}
        </div>
      )}
    </div>
  );
};
